import React, { useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { Button, Modal, Tabs } from 'antd';
import { isNil } from 'lodash';

import useTabs from 'hooks/useTabs';
import { TabValidationStatuses } from 'enums';
import BaseAlert from 'components/wrappers/BaseAlert';
import { getTabValidationIcon } from 'utils/tabUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { updateAssignmentAdditionalInfo } from 'redux/reducers/assignmentSlice';
import {
  AssignmentAdminAdditionalInfoModel,
  AssignmentAdminDocumentModel,
} from 'api/adminApiClient';
import AdditionalInfo, { AdditionalInfoFormValues } from './AdditionalInfo';
import { useAppDispatch } from 'hooks/useAppDispatch';

type Props = {
  onClose: () => void;
  additionalInfo: AssignmentAdminAdditionalInfoModel;
  assignmentId: string;
  onSubmit: () => void;
};

enum TabKeys {
  AdditionalInfo = 0,
}

const getAssignmentAdditionalTabs = () => [
  {
    title: 'Additional information',
    status: TabValidationStatuses.Wait,
  },
];

const mapAssignmentAdditionalInfoToRequest = (
  additionalInfo: AdditionalInfoFormValues,
) => {
  const {
    markup,
    grossMarginPercent,
    grossMarginPerHour,
    anticipatedWeeklyCommission,
    confirmationCommissionStructure,
  } = additionalInfo;
  return {
    additionalInfo: {
      markup: !isNil(markup) ? markup : undefined,
      grossMarginPercent: !isNil(grossMarginPercent)
        ? grossMarginPercent
        : undefined,
      grossMarginPerHour: !isNil(grossMarginPerHour)
        ? grossMarginPerHour
        : undefined,
      anticipatedWeeklyCommission: !isNil(anticipatedWeeklyCommission)
        ? anticipatedWeeklyCommission
        : undefined,
      confirmationCommissionStructure:
        confirmationCommissionStructure || undefined,
    },
  };
};

type AdditionalInfoFormikProps = FormikProps<AdditionalInfoFormValues>;

export default function EditAssignmentAdditionalInfoModal(props: Props) {
  const { onClose, additionalInfo, assignmentId, onSubmit } = props;
  const dispatch = useAppDispatch();
  const [tabs, onFormChange] = useTabs(getAssignmentAdditionalTabs());
  const [isLoading, setIsLoading] = useState(false);
  const additionalInfoFormRef: any = useRef();

  const isFormValid = () => {
    return [additionalInfoFormRef.current?.isValid].every(Boolean);
  };

  const isFormSubmitting = () => {
    return [additionalInfoFormRef.current?.isSubmitting].every(Boolean);
  };

  const isFormDirty = () => {
    return [additionalInfoFormRef.current?.dirty].some(Boolean);
  };

  const resetFormSubmitting = () => {
    [additionalInfoFormRef.current].forEach((form) =>
      form.setSubmitting(false),
    );
  };

  const handleSubmit = async () => {
    const additionalInfoForm: AdditionalInfoFormikProps =
      additionalInfoFormRef.current;

    await Promise.all([additionalInfoForm.submitForm()]);

    if (!isFormValid()) return;

    setIsLoading(true);

    const data: AssignmentAdminDocumentModel =
      mapAssignmentAdditionalInfoToRequest(additionalInfoForm.values);

    const result = await dispatch(
      updateAssignmentAdditionalInfo({ assignmentId, data }),
    );

    if (updateAssignmentAdditionalInfo.fulfilled.match(result)) {
      await onSubmit();
      onClose();
      BaseAlert.success('Assignment additional information has been updated.');
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);

      resetFormSubmitting();
    }

    setIsLoading(false);
  };

  const handleModalCancel = () => {
    if (isFormSubmitting()) return;

    if (isFormDirty()) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  return (
    <BaseModal
      title="Edit assignment additional information"
      maxWidth={888}
      open={true}
      closable={true}
      onCancel={handleModalCancel}
      className="modal-with-tabs"
    >
      <Tabs className="icon-tabs modal-scroll-wrapper">
        <Tabs.TabPane
          tab={
            <div className="tab-pane-wrapper">
              <span className="tab-title">
                {tabs[TabKeys.AdditionalInfo].title}
              </span>
              {getTabValidationIcon(tabs[TabKeys.AdditionalInfo].status)}
            </div>
          }
        >
          <AdditionalInfo
            formRef={additionalInfoFormRef}
            additionalInfo={additionalInfo}
            onChange={onFormChange[TabKeys.AdditionalInfo]}
          />
        </Tabs.TabPane>
      </Tabs>
      <BaseModalFooter>
        <Button onClick={handleModalCancel}>Cancel</Button>
        <Button
          type="primary"
          data-id="editAssignmentAdditionalInfoBtn"
          disabled={isLoading}
          loading={isLoading}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </BaseModalFooter>
    </BaseModal>
  );
}
